import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  lazy,
  Suspense
} from 'react';
import { useParams } from 'react-router-dom';
import DraggableOverviewPanel from './components/draggable-overview-panel';
import TripInfoOverview from './panels/trip-info-overview';
import TripIdeasItinerariesOverview from './panels/trip-ideas-itineraries-overview';
import TripMapOverview from './panels/trip-map-overview';
import TripRecapOverview from './panels/trip-recap-overview';
import TripCommentsOverview from './panels/trip-comments-overview';
import tripsService from '../../../services/trips/trips.service';
import { useTrip } from '../../contexts/trip-context';
import './trip-overview.scss';

// Lazy load full views for better runtime performance
const TripIdeasDiscoveryView = lazy(
  () => import('./full-views/trip-ideas-discovery-view')
);
const TripItinerariesView = lazy(
  () => import('./full-views/trip-itineraries-view')
);
const TripRecapView = lazy(() => import('./full-views/trip-recap-view'));
const TripInfoView = lazy(() => import('./full-views/trip-info-view'));
const TripPhotosViewWrapper = lazy(
  () => import('./full-views/trip-photos-view-wrapper')
);
const TripToursView = lazy(() => import('./full-views/trip-tours-view'));

const DEFAULT_PANEL_ORDER = [
  'info',
  'map',
  'ideas-itineraries',
  'comments',
  'recap'
];
const STORAGE_KEY_PREFIX = 'trip-overview-layout';
const SAVE_DEBOUNCE_MS = 1000;

// Map panel keys to section keys for navigation
const PANEL_TO_SECTION: Record<string, string> = {
  info: 'information',
  map: 'map',
  'ideas-itineraries': 'ideas-itineraries',
  comments: 'comments',
  recap: 'recap'
};

const TripOverview: React.FC = () => {
  const { id: tripId, section } = useParams<{ id: string; section?: string }>();
  const { trip } = useTrip();
  const [panelOrder, setPanelOrder] = useState<string[]>(DEFAULT_PANEL_ORDER);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout>();

  // Load saved layout on mount
  useEffect(() => {
    if (!tripId) return;

    const loadLayout = () => {
      // Try to load from trip data (database)
      if (trip?.overviewLayout && Array.isArray(trip.overviewLayout)) {
        setPanelOrder(trip.overviewLayout);
        // Update localStorage cache
        localStorage.setItem(
          `${STORAGE_KEY_PREFIX}-${tripId}`,
          JSON.stringify(trip.overviewLayout)
        );
        return;
      }

      // Fallback to localStorage
      const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}-${tripId}`);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed) && parsed.length === 5) {
            setPanelOrder(parsed);
            return;
          }
        } catch (error) {
          console.error('Error parsing stored layout:', error);
        }
      }

      // Use default order
      setPanelOrder(DEFAULT_PANEL_ORDER);
    };

    loadLayout();
  }, [tripId, trip]);

  // Save layout to database and localStorage (debounced)
  const saveLayout = useCallback(
    (newOrder: string[]) => {
      if (!tripId) return;

      // Save to localStorage immediately
      localStorage.setItem(
        `${STORAGE_KEY_PREFIX}-${tripId}`,
        JSON.stringify(newOrder)
      );

      // Debounce database save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(async () => {
        try {
          await tripsService.updateTrip(tripId, {
            overviewLayout: newOrder
          } as any);
        } catch (error) {
          console.error('Error saving layout to database:', error);
        }
      }, SAVE_DEBOUNCE_MS);
    },
    [tripId]
  );

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      const newOrder = [...panelOrder];
      // Swap the dragged panel with the drop target panel
      [newOrder[draggedIndex], newOrder[index]] = [
        newOrder[index],
        newOrder[draggedIndex]
      ];
      setPanelOrder(newOrder);
      saveLayout(newOrder);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const renderPanelContent = (panelKey: string) => {
    if (!trip) {
      return (
        <div className='panel-placeholder'>
          <p>Loading...</p>
        </div>
      );
    }

    switch (panelKey) {
      case 'info':
        return <TripInfoOverview trip={trip} />;
      case 'map':
        return <TripMapOverview trip={trip} />;
      case 'ideas-itineraries':
        return <TripIdeasItinerariesOverview trip={trip} />;
      case 'comments':
        return <TripCommentsOverview trip={trip} />;
      case 'recap':
        return <TripRecapOverview trip={trip} />;
      default:
        return (
          <div className='panel-placeholder'>
            <p>Unknown panel</p>
          </div>
        );
    }
  };

  // If a specific section is requested, render the full view
  if (section && section !== 'overview') {
    if (!trip) {
      return (
        <div className='section-loading'>
          <div
            className='spinner-border text-primary'
            role='status'
          >
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      );
    }

    // Wrap lazy-loaded components in Suspense
    const LoadingFallback = (
      <div className='section-loading'>
        <div
          className='spinner-border text-primary'
          role='status'
        >
          <span className='visually-hidden'>Loading view...</span>
        </div>
      </div>
    );

    switch (section) {
      case 'information':
        return (
          <Suspense fallback={LoadingFallback}>
            <TripInfoView />
          </Suspense>
        );
      case 'discovery':
        return (
          <Suspense fallback={LoadingFallback}>
            <TripIdeasDiscoveryView />
          </Suspense>
        );
      case 'itineraries':
        return (
          <Suspense fallback={LoadingFallback}>
            <TripItinerariesView />
          </Suspense>
        );
      case 'recap':
        return (
          <Suspense fallback={LoadingFallback}>
            <TripRecapView />
          </Suspense>
        );
      case 'photos':
        return (
          <Suspense fallback={LoadingFallback}>
            <TripPhotosViewWrapper />
          </Suspense>
        );
      case 'tours':
        return (
          <Suspense fallback={LoadingFallback}>
            <TripToursView />
          </Suspense>
        );
      default:
        return (
          <div className='section-not-found'>
            <h3>Section not found</h3>
            <p>The requested section "{section}" does not exist.</p>
          </div>
        );
    }
  }

  return (
    <div className='trip-overview'>
      <div className='trip-overview-grid'>
        {panelOrder.map((panelKey, index) => (
          <DraggableOverviewPanel
            key={panelKey}
            index={index}
            panelKey={PANEL_TO_SECTION[panelKey] || panelKey}
            isDragging={draggedIndex === index}
            isDragOver={dragOverIndex === index}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
          >
            {renderPanelContent(panelKey)}
          </DraggableOverviewPanel>
        ))}
      </div>
    </div>
  );
};

export default TripOverview;
